import type { ApiAuthType } from '@/app/api/auth/[...everything]+api';
import { Text } from '@/components/ui/text';
import { H2 } from '@/components/ui/typography';
import { useAuth } from '@/contexts/AuthContext';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { generateAPIUrl } from '@/utils/api-url';
import { skToPk } from '@/utils/encryption';
import { tokenCache } from '@/utils/token-cache';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { router } from 'expo-router';
import Head from 'expo-router/head';
import { fetch as expoFetch } from 'expo/fetch';
import { hc } from 'hono/client';
import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  View
} from 'react-native';
import { OtpInput } from 'react-native-otp-entry';
import { SafeAreaView } from 'react-native-safe-area-context';

const LOGO = require('../../assets/images/icon-dark.png');

const client = hc<ApiAuthType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

async function submitOtp(otpId: string, otp: string) {
  const res = await client.api.auth.verify.$post({ json: { otpId, otp } });

  const data = await res.json();
  if (data.valid !== true) {
    throw new Error(
      typeof data.error === 'string' ? data.error : 'Failed to verify'
    );
  }

  return data;
}

export default function VerifyAuth() {
  const [otpId, setOtpId] = useState<undefined | string>(undefined);
  const [email, setEmail] = useState('');

  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const { colorScheme } = useColorScheme();
  const { updateAuthState } = useAuth();

  const submitOtpCallback = useCallback(
    (submittedOtp?: unknown) => {
      if (!otpId) {
        return;
      }

      setLoading(true);
      submitOtp(otpId, typeof submittedOtp === 'string' ? submittedOtp : otp)
        .then((data) => {
          if (data?.token) {
            updateAuthState?.({
              authState: {
                encryptionKey: skToPk(data.secretKey),
                decryptionKey: data.secretKey,
                token: data.token,
                authenticated: true,
                loading: false
              },
              user: data.user
            });
            return Promise.all([
              AsyncStorage.removeItem('otp'),
              AsyncStorage.removeItem('email'),
              tokenCache.saveToken('auth-token', data.token)
            ]);
          }
          throw new Error('Failed to verify');
        })
        .then(() => {
          setLoading(false);
          router.dismissAll();
          router.replace('/app');
        })
        .catch((e) => {
          alert(e instanceof Error ? e.message : 'Failed to verify');
          setLoading(false);
        });
    },
    [otpId, otp]
  );

  useEffect(() => {
    AsyncStorage.getItem('otp').then((maybeOtpId) => {
      if (maybeOtpId) {
        setOtpId(maybeOtpId);
      } else {
        router.navigate('/auth/login');
      }
    });
  }, []);

  useEffect(() => {
    AsyncStorage.getItem('email').then((maybeEmail) => {
      if (maybeEmail) {
        setEmail(maybeEmail);
      }
    });
  }, []);

  return (
    <Fragment>
      <Head>
        <title>Verify - Lexikon AI</title>
      </Head>
      <SafeAreaView
        edges={['right', 'bottom', 'left']}
        className="flex-1 web:w-full web:self-center web:lg:w-[50%]"
      >
        <ScrollView contentInsetAdjustmentBehavior="always">
          <View className="flex-1 px-8">
            <View className="native:pt-8 items-center pb-1">
              <Image source={LOGO} style={styles.logo} resizeMode="contain" />

              <H2 className="border-0 pb-1 pt-4 text-center">
                Check your email
              </H2>
              <Text className="pt-4 text-center">
                <Text>
                  We sent a code to{' '}
                  {
                    <Text className="font-medium">
                      {email.length > 50 ? `${email.slice(0, 50)}…` : email}
                    </Text>
                  }
                  . The code expires shortly, so please enter it soon.
                </Text>
              </Text>
            </View>
            <View className="pt-4">
              <View className="max-w-96 self-center web:max-w-full web:lg:w-[50%]">
                <OtpInput
                  numberOfDigits={6}
                  focusColor={colorScheme === 'dark' ? '#eee' : '#000'}
                  autoFocus
                  disabled={loading}
                  type="alphanumeric"
                  onTextChange={setOtp}
                  onFilled={submitOtpCallback}
                  textInputProps={{
                    accessibilityLabel: 'One-Time Password'
                  }}
                  theme={{
                    pinCodeContainerStyle: {
                      backgroundColor: colorScheme === 'dark' ? '#333' : '#333',
                      borderColor: colorScheme === 'dark' ? '#444' : '#444',
                      marginRight: Platform.OS === 'web' ? 5 : undefined
                    },
                    focusedPinCodeContainerStyle: {
                      backgroundColor: colorScheme === 'dark' ? '#444' : '#444',
                      borderColor: colorScheme === 'dark' ? '#444' : '#444'
                    },
                    pinCodeTextStyle: {
                      color: colorScheme === 'dark' ? '#fff' : '#fff'
                    },
                    focusStickStyle: {
                      backgroundColor: colorScheme === 'dark' ? '#fff' : '#fff'
                    },
                    placeholderTextStyle: {
                      color: colorScheme === 'dark' ? '#999' : '#999'
                    },
                    disabledPinCodeContainerStyle: {
                      backgroundColor: colorScheme === 'dark' ? '#333' : '#333'
                    }
                    // pinCodeContainerStyle: styles.pinCodeContainer,
                    // pinCodeTextStyle: styles.pinCodeText,
                    // focusStickStyle: styles.focusStick,
                    // focusedPinCodeContainerStyle: styles.activePinCodeContainer,
                    // placeholderTextStyle: styles.placeholderText,
                    // filledPinCodeContainerStyle: styles.filledPinCodeContainer,
                    // disabledPinCodeContainerStyle: styles.disabledPinCodeContainer,
                  }}
                />
              </View>
            </View>
            <View className="self-stretch py-4">
              {/* <SubmitButton loading={loading} onPress={submitOtpCallback} /> */}
              {loading ? (
                <View className="py-1">
                  <ActivityIndicator color="black" />
                </View>
              ) : null}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Fragment>
  );
}

// function SubmitButton({ loading, onPress }: { loading: boolean; onPress: () => void }) {
//   return (
//     <Button size="lg" className="rounded-full" onPress={onPress} disabled={loading}>
//       {loading ? (
//         <View className="py-1">
//           <ActivityIndicator color="black" />
//         </View>
//       ) : (
//         <Text>Verify</Text>
//       )}
//     </Button>
//   );
// }

const styles = StyleSheet.create({
  logo: {
    height: 100
  }
});
